import React from "react";
import PropTypes from "prop-types";
import { FaRegTrashAlt } from "react-icons/fa";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Button,
  Flex,
  HStack,
  VStack,
} from "@chakra-ui/react";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { GENRE, STORY_RELAY_LEVELS } from "utils/data/options";

const genreOptions = GENRE;
const relayLevels = STORY_RELAY_LEVELS;

const RelayPromptForm = ({ data, updatePrompt, isDisabled }) => {
  // Validation schema
  const validationSchema = Yup.object().shape({
    // shortPrompt: Yup.string().required('Short prompt is required'),
    prompt: Yup.string().max(800, "Prompt must not exceed 800 characters"),
    genre: Yup.string().required("Genre is required"),
    level: Yup.string().required("Level is required"),
    segmentLength: Yup.string().required("Segment Length is required"),
  });

  return (
    <Formik
      initialValues={data}
      validationSchema={validationSchema}
      enableReinitialize
      // onSubmit={(values) => {
      //   console.log('update prompt values: ', values);
      //   updatePrompt(values);
      // }}
    >
      {({ values, errors, touched }) => (
        <Form>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Story Title</FormLabel>
              <Field
                as={Input}
                id="promptTitle"
                name="promptTitle"
                placeholder="Story Title"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Starter</FormLabel>
              <Field
                as={Input}
                id="starter"
                name="starter"
                placeholder="Story starts with ..."
              />
            </FormControl>

            <FormControl isInvalid={errors.instruction && touched.instruction}>
              <FormLabel>Instruction</FormLabel>
              <Field
                as={Textarea}
                name="instruction"
                maxLength={500}
                rows={4}
              />
            </FormControl>

            {/* Genre and Pattern */}
            <Flex gap={4}>
              <FormControl isRequired isInvalid={errors.genre && touched.genre}>
                <FormLabel>Genre</FormLabel>

                <Field
                  as={Select}
                  id="genre"
                  name="genre"
                  placeholder="Select Genre"
                >
                  {genreOptions.map((genre) => (
                    <option key={genre.value} value={genre.value}>
                      {genre.name}
                    </option>
                  ))}
                </Field>
              </FormControl>
              <FormControl isRequired isInvalid={errors.level && touched.level}>
                <FormLabel>Level</FormLabel>
                <Field
                  as={Select}
                  id="level"
                  name="level"
                  placeholder="Select Level"
                >
                  {relayLevels.map((level) => (
                    <option key={level.id} value={level.id}>
                      {level.name}
                    </option>
                  ))}
                </Field>
              </FormControl>

              <FormControl
                isRequired
                isInvalid={errors.segmentLength && touched.segmentLength}
              >
                <FormLabel>Segment length</FormLabel>
                <Field
                  as={Select}
                  id="segmentLength"
                  name="segmentLength"
                  placeholder="Select Segment Length"
                >
                  <option value="paragraph">paragraph</option>
                  <option value="sentence">sentence</option>
                </Field>
              </FormControl>
            </Flex>

            <FormControl>
              <FormLabel>Illustration</FormLabel>
              <Field as={Input} name="illustration" placeholder="https://" />
            </FormControl>

            <FormControl>
              <FormLabel>Thumbnail</FormLabel>
              <Field as={Input} name="thumbnailUrl" placeholder="https://" />
            </FormControl>

            <Flex gap={4}>
              <FormControl>
                <FormLabel>Max Word Count</FormLabel>
                <Field as={Input} type="number" name="maxWordCount" />
              </FormControl>

              <FormControl>
                <FormLabel>Sprint in Minutes</FormLabel>
                <Field as={Input} type="number" name="sprintInMinutes" />
              </FormControl>
            </Flex>

            <Flex gap={4}>
              <FormControl>
                <FormLabel>Min Segment Word Count</FormLabel>
                <Field as={Input} type="number" name="minSegmentWordCount" />
              </FormControl>

              <FormControl>
                <FormLabel>Max Segment Word Count</FormLabel>
                <Field as={Input} type="number" name="maxSegmentWordCount" />
              </FormControl>
            </Flex>

            <Flex gap={4}>
              <FormControl>
                <FormLabel>Min Round Count</FormLabel>
                <Field as={Input} type="number" name="minRound" />
              </FormControl>

              <FormControl>
                <FormLabel>Max Round Count</FormLabel>
                <Field as={Input} type="number" name="maxRound" />
              </FormControl>
            </Flex>

            <Flex gap={4}>
              <FormControl>
                <FormLabel>Type</FormLabel>
                <Field
                  as={Select}
                  id="style"
                  name="style"
                  placeholder="Select Style"
                >
                  <option value="co writing">Co-writing</option>
                  <option value="role play">Role play</option>
                  <option value="debate">Debate</option>
                </Field>
              </FormControl>

              <FormControl>
                <FormLabel>Tags</FormLabel>
                <FieldArray
                  name="tags"
                  render={(arrayHelpers) => (
                    <Box>
                      {values.tags?.map((tag, index) => (
                        <HStack key={index} spacing={4} align="center" mb={2}>
                          <Field
                            as={Input}
                            name={`tags[${index}]`}
                            placeholder="Tag"
                          />
                          <FaRegTrashAlt
                            color="red"
                            onClick={() => arrayHelpers.remove(index)}
                          />
                        </HStack>
                      ))}
                      <Button
                        size="sm"
                        onClick={() => arrayHelpers.push("")}
                        colorScheme="teal"
                      >
                        Add Tag
                      </Button>
                    </Box>
                  )}
                />
              </FormControl>
            </Flex>

            <HStack justifyContent="flex-end" spacing={4}>
              <Button
                disabled={isDisabled}
                variant="outline"
                colorScheme="green"
                size="md"
                mb={8}
                onClick={() => updatePrompt(values, 1)}
              >
                Save
              </Button>
              <Button
                disabled={isDisabled}
                colorScheme="green"
                size="md"
                mb={8}
                onClick={() => updatePrompt(values, 2)}
              >
                Submit
              </Button>
            </HStack>
          </VStack>
        </Form>
      )}
    </Formik>
  );
};

RelayPromptForm.propTypes = {
  updatePrompt: PropTypes.func.isRequired,
  // isEdit: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.arrayOf(PropTypes.string),
    ])
  ),
};

export default RelayPromptForm;
